import * as moment from 'moment';
import * as _ from 'lodash';
import { useMemo, useState, useEffect } from 'react';
import './audit-log-panel.css';

import { IWatrRequestAuditLog, IWatrRequest, IUser, IListItem } from '@nkr/models/index';
import AuditEntry from './audit-entry';
import { Button, Panel, Table, Well } from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';
import { FileDocumentType } from '@nkr/enums/document-type';
import { WageAndTravelStatus } from '@nkr/enums/watr-status';
import { AdvancedWorkflowStatus } from '@nkr/enums/index';
import { downloadFile } from '@nkr/services/download-file';
import {
    AUDIT_ACTION_CATEGORIES,
    AUDIT_ACTION_FILTER_ALL,
    AUDIT_ACTION_FILTER_UNCLASSIFIED,
    AUDIT_ACTION_CATEGORY,
    AUDIT_APPROVAL_SUB_FILTER,
    AUDIT_FILTER_NEUTRAL_COLORS,
    AuditActionCategoryId,
    AuditApprovalSubFilterId,
    ADVANCED_WORKFLOW_AUDIT_LABEL_BY_CODE,
    formatAuditLogDetailMessage,
    getActionDisplayParts,
    getCategoryBadgeStyle,
    getCategoryForLog,
    getDetailToneStyles,
    getNeutralFilterChipStyle,
    IAuditActionCategoryDefinition,
    IAuditActionDisplayParts,
    logMatchesActionCategoryFilter,
    shouldShowActivityDetailLabel
} from './audit-log-action-groups';

const ADVANCED_WORKFLOW_TRANSITION_PREFIX = 'AdvancedWorkflowTransition:';

interface IProps {
    data: IWatrRequest,
    container: any,
    currentUser: IUser,
    auditLogTypes: IListItem[],
    logActionTypes?: IListItem[],
    focusDeclinedToken?: number,
    onSaveLogEntry: (type: number, comments: string, file: File) => void,
    onUpdateLogEntry: (id: number, type: number, comments: string, file: File) => void,
    onDeleteLogEntry: (logId: number) => void,
    downloadFile: (docType: number, lookupId: number) => Promise<any>
}

const AUDIT_LOG_DATE_PARSE_FORMATS = [
    moment.ISO_8601,
    'MM/DD/YYYY HH:mm:ss',
    'MM/DD/YYYY HH:mm',
    'MM/DD/YYYY h:mm A',
    'MM/DD/YYYY',
    'MM/dd/yyyy HH:mm:ss',
    'MM/dd/yyyy HH:mm',
    'MM/dd/yyyy h:mm tt',
    'MM/dd/yyyy'
];
const getAuditLogMoment = (log: IWatrRequestAuditLog): moment.Moment | null => {
    const dateValue = log.logDate || log.updatedDate;
    if (!dateValue) {
        return null;
    }

    const strict = moment(dateValue, AUDIT_LOG_DATE_PARSE_FORMATS, true);
    if (strict.isValid()) {
        return strict;
    }

    const loose = moment(dateValue);
    return loose.isValid() ? loose : null;
};

const auditLogSourceHasTime = (raw: string, parsed: moment.Moment) => {
    if (/:\d{2}/.test(raw) || /T\d{1,2}:/i.test(raw)) {
        return true;
    }
    return !parsed.isSame(parsed.clone().startOf('day'));
};

const getAuditLogWhenParts = (log: IWatrRequestAuditLog): { dateText: string; timeText: string | null; actor: string } => {
    const parsed = getAuditLogMoment(log);
    const actor = formatAuditLogUpdatedBy(log);
    if (!parsed) {
        return { dateText: '—', timeText: null, actor };
    }

    const raw = (log.logDate || log.updatedDate || '').trim();
    const hasTime = auditLogSourceHasTime(raw, parsed);
    return {
        dateText: parsed.format('MM/DD/YYYY'),
        timeText: hasTime ? parsed.format('h:mm A') : null,
        actor
    };
};

const renderAuditLogWhenCell = (log: IWatrRequestAuditLog) => {
    const { dateText, timeText, actor } = getAuditLogWhenParts(log);
    return (
        <div className='watr-audit-log-when'>
            <div className='watr-audit-log-when-date'>
                {dateText}
            </div>
            {timeText != null && (
                <div className='watr-audit-log-when-time'>
                    {timeText}
                </div>
            )}
            <div className='watr-audit-log-when-actor'>
                {actor}
            </div>
        </div>
    );
};

const formatAuditLogUpdatedBy = (log: IWatrRequestAuditLog) => {
    const alias = (log.updatedByAlias || '').trim();
    const userId = log.userId ?? 0;
    if (userId > 0 && alias.length > 0 && alias.toLowerCase() !== 'unknown') {
        return alias;
    }
    if (alias.length > 0 && alias.toLowerCase() !== 'unknown') {
        return alias;
    }
    if (log.logType === 0) {
        return 'System';
    }
    if (alias.length === 0 || alias.toLowerCase() === 'unknown') {
        return 'System';
    }
    return alias;
};

const getLogStatusValues = (log: IWatrRequestAuditLog) => {
    const prev = log.prevStatus ?? log.originalStatus;
    const curr = log.currentStatus ?? log.updatedStatus;
    return { prev, curr };
};

const getLogStatusLabels = (log: IWatrRequestAuditLog) => {
    const { prev, curr } = getLogStatusValues(log);
    const prevLabel =
        formatWatrStatusLabel(prev) ??
        (log.originalStatusDescription?.trim() || null);
    const currLabel =
        formatWatrStatusLabel(curr) ??
        (log.updatedStatusDescription?.trim() || null);
    return { prev, curr, prevLabel, currLabel };
};

const parseAdvancedWorkflowTransition = (
    logMessage: string | undefined
): { from: AdvancedWorkflowStatus; to: AdvancedWorkflowStatus } | null => {
    if (!logMessage || !logMessage.startsWith(ADVANCED_WORKFLOW_TRANSITION_PREFIX)) {
        return null;
    }

    const body = logMessage.substring(ADVANCED_WORKFLOW_TRANSITION_PREFIX.length).trim();
    const transitionSegment = body.split(';')[0]?.trim() ?? '';
    const parts = transitionSegment.split('->').map(part => part.trim());
    if (parts.length !== 2) {
        return null;
    }

    const fromRaw = parts[0] as keyof typeof AdvancedWorkflowStatus;
    const toRaw = parts[1] as keyof typeof AdvancedWorkflowStatus;
    const from = AdvancedWorkflowStatus[fromRaw];
    const to = AdvancedWorkflowStatus[toRaw];
    if (typeof from !== 'number' || typeof to !== 'number') {
        return null;
    }
    return { from, to };
};

const formatAdvancedWorkflowAuditLabel = (status: AdvancedWorkflowStatus): string => {
    return ADVANCED_WORKFLOW_AUDIT_LABEL_BY_CODE[status] ?? String(status);
};

const formatWatrStatusLabel = (status: number | string | null | undefined): string | null => {
    if (status === null || status === undefined) {
        return null;
    }
    if (typeof status === 'string') {
        const trimmed = status.trim();
        if (!trimmed) {
            return null;
        }

        const asNum = Number(trimmed);
        if (!Number.isNaN(asNum) && String(asNum) === trimmed) {
            return formatWatrStatusLabel(asNum);
        }
        return trimmed;
    }

    const enumName = WageAndTravelStatus[status];
    if (typeof enumName === 'string') {
        return enumName.replace(/([a-z])([A-Z])/g, '$1 $2');
    }
    return `Status ${status}`;
};

interface IAuditLogStatusChange {
    fromLabel: string;
    toLabel: string;
}

interface IAuditLogStatusDisplay {
    text: string | null;
    hasChange: boolean;
    changes?: IAuditLogStatusChange[];
    fromLabel?: string;
    toLabel?: string;
}

const renderStatusChangePill = (fromLabel: string, toLabel: string) => (
    <span
        style={{
            display: 'block',
            padding: '3px 8px',
            borderRadius: 3,
            backgroundColor: '#e8f4fc',
            borderLeft: '3px solid #5bc0de',
            lineHeight: 1.35,
            marginBottom: 4
        }}
    >
        <span style={{ color: '#555' }}>{fromLabel}</span>
        <span style={{ margin: '0 6px', color: '#888' }}>→</span>
        <span style={{ fontWeight: 600, color: '#245269' }}>{toLabel}</span>
    </span>
);
const renderAuditLogStatusCell = (display: IAuditLogStatusDisplay) => {
    if (!display.hasChange) {
        return <span style={{ color: '#999' }}>—</span>;
    }
    if (display.changes && display.changes.length > 1) {
        return (
            <div>
                {display.changes.map((change, index) => (
                    <span key={`${change.fromLabel}-${change.toLabel}-${index}`}>
                        {renderStatusChangePill(change.fromLabel, change.toLabel)}
                    </span>
                ))}
            </div>
        );
    }

    const fromLabel = display.changes?.[0]?.fromLabel ?? display.fromLabel;
    const toLabel = display.changes?.[0]?.toLabel ?? display.toLabel;
    if (!fromLabel || !toLabel) {
        return <span style={{ color: '#999' }}>—</span>;
    }
    return renderStatusChangePill(fromLabel, toLabel);
};

const renderAuditLogActivityCell = (
    log: IWatrRequestAuditLog,
    parts: IAuditActionDisplayParts,
    statusDisplay: IAuditLogStatusDisplay
) => {
    const toneStyles = parts.isClassified
        ? getDetailToneStyles(parts.detailTone)
        : { color: '#999', fontWeight: 400 as number };
    const hasStatus = statusDisplay.hasChange;
    const showDetail = shouldShowActivityDetailLabel(log, parts, hasStatus);
    const detailText = showDetail && parts.isClassified && parts.detailLabel.length > 0
        ? parts.detailLabel
        : null;
    if (!detailText && !hasStatus) {
        return <span style={{ color: '#999' }}>—</span>;
    }

    return (
        <div className='watr-audit-log-event'>
            {detailText &&
                <div className='watr-audit-log-event-title' style={{ ...toneStyles, marginBottom: hasStatus ? 8 : 0 }}>
                    {detailText}
                </div>
            }
            {hasStatus &&
                <div>
                    <div className='watr-audit-log-event-status-label'>
                        Request Status
                    </div>
                    {renderAuditLogStatusCell(statusDisplay)}
                </div>
            }
        </div>
    );
};

const getAuditLogStatusDisplay = (
    log: IWatrRequestAuditLog,
    _requestCurrentStatusLabel: string | null
): IAuditLogStatusDisplay => {
    const { prev, curr, prevLabel, currLabel } = getLogStatusLabels(log);
    const watrStatusChanged =
        prev != null &&
        curr != null &&
        prev !== curr &&
        prevLabel != null &&
        currLabel != null &&
        prevLabel !== currLabel;
    const advancedTransition = parseAdvancedWorkflowTransition(log.logMessage);
    const advancedStatusChanged =
        advancedTransition != null && advancedTransition.from !== advancedTransition.to;
    const changes: IAuditLogStatusChange[] = [];
    if (watrStatusChanged) {
        changes.push({ fromLabel: prevLabel!, toLabel: currLabel! });
    }
    if (advancedStatusChanged) {
        changes.push({
            fromLabel: formatAdvancedWorkflowAuditLabel(advancedTransition.from),
            toLabel: formatAdvancedWorkflowAuditLabel(advancedTransition.to)
        });
    }
    if (changes.length === 0) {
        return { text: null, hasChange: false };
    }
    if (changes.length === 1) {
        const only = changes[0];
        return {
            text: `${only.fromLabel} → ${only.toLabel}`,
            hasChange: true,
            fromLabel: only.fromLabel,
            toLabel: only.toLabel,
            changes
        };
    }
    return {
        text: changes.map(c => `${c.fromLabel} → ${c.toLabel}`).join(' / '),
        hasChange: true,
        changes
    };
};

const AuditLog = (props: IProps) => {
    const AUDIT_SOURCE_FILTER_ALL = -1;
    const AUDIT_SOURCE_FILTER_FILES_ONLY = -2;
    const [auditFilterType, setAuditFilterType] = useState(AUDIT_SOURCE_FILTER_ALL);
    const [auditFilterActionCategory, setAuditFilterActionCategory] = useState<AuditActionCategoryId>(
        AUDIT_ACTION_FILTER_ALL
    );
    const [auditApprovalSubFilter, setAuditApprovalSubFilter] = useState<AuditApprovalSubFilterId>(
        AUDIT_APPROVAL_SUB_FILTER.All
    );
    const [addEdit, setAddEdit] = useState<{ show: boolean, data: IWatrRequestAuditLog }>({ show: false, data: null });

    useEffect(() => {
        setAuditFilterType(AUDIT_SOURCE_FILTER_ALL);
        setAuditFilterActionCategory(AUDIT_ACTION_FILTER_ALL);
        setAuditApprovalSubFilter(AUDIT_APPROVAL_SUB_FILTER.All);
    }, [props.data?.id]);

    useEffect(() => {
        if (props.focusDeclinedToken == null || props.focusDeclinedToken <= 0) {
            return;
        }
        setAuditFilterType(AUDIT_SOURCE_FILTER_ALL);
        setAuditFilterActionCategory(AUDIT_ACTION_CATEGORY.ApprovalProcess);
        setAuditApprovalSubFilter(AUDIT_APPROVAL_SUB_FILTER.Declined);
    }, [props.focusDeclinedToken]);

    const handleShowAdd = () => {
        setAddEdit({
            show: true,
            data: null
        });
    };

    const handleCancelAdd = () => {
        setAddEdit({
            show: false,
            data: null
        });
    };

    const handelSaveLogEntry = (type: number, comments: string, file: any) => {
        props.onSaveLogEntry(type, comments, file);
        setAddEdit({
            show: false,
            data: null
        });
    };

    const handleDownloadFile = (docType: FileDocumentType, lookupId: number) => {
        props.downloadFile(docType, lookupId)
            .then((response: any) => {
                let fileName = props.data.firstName + '_' + props.data.lastName
                    + "_" + FileDocumentType[docType].toString()
                    + "_" + moment().format("YYYY_MM_DD");
                downloadFile(response.data, fileName, response.data.type);
            });
    };

    const logMatchesSourceFilter = (log: IWatrRequestAuditLog, sourceFilter: number) => {
        if (sourceFilter === AUDIT_SOURCE_FILTER_ALL) {
            return true;
        }
        if (sourceFilter === AUDIT_SOURCE_FILTER_FILES_ONLY) {
            return log.fileLoc != undefined;
        }
        return log.logType === sourceFilter;
    };

    const matchesAuditLogSourceFilter = (log: IWatrRequestAuditLog) =>
        logMatchesSourceFilter(log, auditFilterType);
    const getFilteredLogs = () => {
        var filteredList = (props.data.logs || []).filter(x => {
            if (!matchesAuditLogSourceFilter(x)) {
                return false;
            }
            if (!logMatchesActionCategoryFilter(x, auditFilterActionCategory, auditApprovalSubFilter)) {
                return false;
            }
            return true;
        });
        return _.orderBy(
            filteredList,
            [
                (log) => {
                    const m = getAuditLogMoment(log);
                    return m && m.isValid() ? m.valueOf() : Number.NEGATIVE_INFINITY;
                },
                (log) => Number(log.id) || 0
            ],
            ['desc', 'desc']
        );
    };

    const selectSourceFilter = (sourceFilter: number) => {
        setAuditFilterType(sourceFilter);
    };

    const selectCategoryFilter = (categoryId: AuditActionCategoryId) => {
        setAuditFilterActionCategory(categoryId);
        setAuditApprovalSubFilter(AUDIT_APPROVAL_SUB_FILTER.All);
    }

    const renderFilterChip = (
        label: string,
        count: number,
        active: boolean,
        onClick: () => void,
        category?: IAuditActionCategoryDefinition,
        title?: string
    ) => (
        <Button
            bsSize='xsmall'
            bsStyle='default'
            onClick={onClick}
            title={title}
            style={{
                ...(category
                    ? getCategoryBadgeStyle(category, active ? 'chip-active' : 'chip-inactive')
                    : getNeutralFilterChipStyle(active ? 'chip-active' : 'chip-inactive')),
                marginRight: 4,
                marginBottom: 4
            }}
        >
            {label} ({count})
        </Button>
    )
    if (props.data == null)
        return null;
    const logsForCounts = (props.data.logs || []).filter(matchesAuditLogSourceFilter);
    const logsForSourceCounts = (props.data.logs || []).filter(x =>
        logMatchesActionCategoryFilter(x, auditFilterActionCategory, AUDIT_APPROVAL_SUB_FILTER.All)
    );
    const sourceFilterCounts = {
        all: logsForSourceCounts.length,
        filesOnly: logsForSourceCounts.filter(x => logMatchesSourceFilter(x, AUDIT_SOURCE_FILTER_FILES_ONLY)).length,
        byTypeId: (props.auditLogTypes || []).reduce<Record<number, number>>((acc, item) => {
            acc[item.id] = logsForSourceCounts.filter(x => logMatchesSourceFilter(x, item.id)).length;
            return acc;
        }, {})
    };

    const categoryFilterCounts = {
        all: logsForCounts.length,
        unclassified: logsForCounts.filter(x => !getActionDisplayParts(x).isClassified).length,
        byCategoryId: AUDIT_ACTION_CATEGORIES.reduce<Record<string, number>>((acc, cat) => {
            acc[cat.id] = logsForCounts.filter(x =>
                logMatchesActionCategoryFilter(x, cat.id, AUDIT_APPROVAL_SUB_FILTER.All)
            ).length;
            return acc;
        }, {})
    };

    const logList = getFilteredLogs();
    const showActivityColumn = auditFilterActionCategory === AUDIT_ACTION_FILTER_ALL;
    const getActiveAuditCategoryLabel = (): string => {
        if (auditFilterActionCategory === AUDIT_ACTION_FILTER_ALL) {
            return 'All';
        }
        if (auditFilterActionCategory === AUDIT_ACTION_FILTER_UNCLASSIFIED) {
            return 'Unclassified';
        }

        const cat = AUDIT_ACTION_CATEGORIES.find(c => c.id === auditFilterActionCategory);
        const base = cat?.filterChipLabel ?? 'Selected category';
        if (
            auditFilterActionCategory === AUDIT_ACTION_CATEGORY.ApprovalProcess
            && auditApprovalSubFilter === AUDIT_APPROVAL_SUB_FILTER.Declined
        ) {
            return `${base} · Declined`;
        }
        return base;
    };

    const getActiveAuditSourceLabel = (): string => {
        if (auditFilterType === AUDIT_SOURCE_FILTER_ALL) {
            return 'All';
        }
        if (auditFilterType === AUDIT_SOURCE_FILTER_FILES_ONLY) {
            return 'Files only';
        }

        const item = (props.auditLogTypes || []).find(x => x.id === auditFilterType);
        return item?.description ?? 'Selected source';
    };

    const auditTableMaxHeightPx = useMemo(() => {
        const rowCount = logList.length;
        if (rowCount <= 0) {
            return 200;
        }

        const viewportCap = typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.58) : 640;
        const byRows = rowCount * 52 + 32;
        return Math.min(640, viewportCap, Math.max(280, byRows));
    }, [logList.length]);
    const auditTablePanelStyle: React.CSSProperties = {
        maxHeight: auditTableMaxHeightPx,
        minHeight: Math.min(280, auditTableMaxHeightPx),
        overflow: 'auto',
        overflowX: 'auto',
        border: '1px solid #ddd',
        borderRadius: 4,
        scrollBehavior: 'smooth'
    };

    const requestCurrentStatusLabel =
        (props.data.statusDescription && props.data.statusDescription.trim()) ||
        formatWatrStatusLabel(props.data.status);
    return (
        <>
            <Panel eventKey='5' className='watr-audit-log-panel'>
                <Panel.Heading style={{ paddingTop: 5, paddingBottom: 5 }}>
                    <Panel.Title toggle style={{ fontSize: 14 }}>Audit Logs</Panel.Title>
                </Panel.Heading>
                <Panel.Body collapsible>
                    {addEdit.show ?
                        <AuditEntry
                            data={addEdit.data}
                            auditLogTypes={props.auditLogTypes}
                            onSave={handelSaveLogEntry}
                            onCancel={handleCancelAdd}
                        />
                        :
                        <>
                            <div className='watr-audit-log-toolbar-panel'>
                            <div className='watr-audit-log-toolbar'>
                                <div className='watr-audit-log-filters'>
                                <div className='watr-audit-log-filter-row'>
                                    <span className='watr-audit-log-filter-label'>Source:</span>
                                    <div className='watr-audit-log-filter-chips'>
                                        {renderFilterChip(
                                            'All',
                                            sourceFilterCounts.all,
                                            auditFilterType === AUDIT_SOURCE_FILTER_ALL,
                                            () => selectSourceFilter(AUDIT_SOURCE_FILTER_ALL)
                                        )}
                                        {(props.auditLogTypes || []).map(item =>
                                            renderFilterChip(
                                                item.description,
                                                sourceFilterCounts.byTypeId[item.id] ?? 0,
                                                auditFilterType === item.id,
                                                () => selectSourceFilter(item.id),
                                                undefined,
                                                item.description
                                            )
                                        )}
                                        {renderFilterChip(
                                            'Files only',
                                            sourceFilterCounts.filesOnly,
                                            auditFilterType === AUDIT_SOURCE_FILTER_FILES_ONLY,
                                            () => selectSourceFilter(AUDIT_SOURCE_FILTER_FILES_ONLY),
                                            undefined,
                                            'Entries with an attached file'
                                        )}
                                    </div>
                                </div>
                                <div className='watr-audit-log-filter-row'>
                                    <span className='watr-audit-log-filter-label'>Category:</span>
                                    <div className='watr-audit-log-filter-chips'>
                                        {renderFilterChip(
                                            'All',
                                            categoryFilterCounts.all,
                                            auditFilterActionCategory === AUDIT_ACTION_FILTER_ALL,
                                            () => selectCategoryFilter(AUDIT_ACTION_FILTER_ALL)
                                        )}
                                        {AUDIT_ACTION_CATEGORIES.map(cat =>
                                            renderFilterChip(
                                                cat.filterChipLabel,
                                                categoryFilterCounts.byCategoryId[cat.id] ?? 0,
                                                auditFilterActionCategory === cat.id,
                                                () => selectCategoryFilter(cat.id),
                                                cat,
                                                cat.label
                                            )
                                        )}
                                        {renderFilterChip(
                                            'Unclassified',
                                            categoryFilterCounts.unclassified,
                                            auditFilterActionCategory === AUDIT_ACTION_FILTER_UNCLASSIFIED,
                                            () => selectCategoryFilter(AUDIT_ACTION_FILTER_UNCLASSIFIED),
                                            undefined,
                                            'Entries that could not be mapped to a category (missing or legacy action type)'
                                        )}
                                    </div>
                                </div>
                                </div>
                                <div className='watr-audit-log-toolbar-actions'>
                                    <Button bsStyle='link' onClick={handleShowAdd}>Add Log Entry</Button>
                                </div>
                            </div>
                            </div>
                            {props.data.logs != null && props.data.logs.length > 0 ?
                                (logList.length > 0 ?
                                    <>
                                    <p className='watr-audit-log-results-meta'>
                                        {logList.length} {logList.length === 1 ? 'entry' : 'entries'} · Newest first
                                    </p>
                                    <div className='watr-audit-log-table-scroll' style={auditTablePanelStyle}>
                                        <Table bordered condensed hover striped={auditFilterActionCategory !== AUDIT_ACTION_FILTER_ALL} className='watr-audit-log-table'>
                                            <colgroup>
                                                <col style={{ width: 128 }} />
                                                {showActivityColumn && <col style={{ width: '36%' }} />}
                                                <col />
                                            </colgroup>
                                            <thead>
                                                <tr>
                                                    <th className='watr-audit-log-col-when'>When</th>
                                                    {showActivityColumn && <th>What Happened</th>}
                                                    <th>Details</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {logList.map(log => {
                                                    const statusDisplay = getAuditLogStatusDisplay(log, requestCurrentStatusLabel);
                                                    const actionParts = getActionDisplayParts(log);
                                                    const category = getCategoryForLog(log);
                                                    const rowStyle = showActivityColumn
                                                        ? {
                                                            backgroundColor: category
                                                                ? category.badgeBackground
                                                                : AUDIT_FILTER_NEUTRAL_COLORS.badgeBackground,
                                                            boxShadow: `inset 4px 0 0 ${(category ? category.badgeBorder : AUDIT_FILTER_NEUTRAL_COLORS.badgeBorder)}`
                                                        }
                                                        : undefined;
                                                    return (
                                                        <tr key={'log-' + log.id} style={rowStyle}>
                                                            <td className='watr-audit-log-col-when'>
                                                                {renderAuditLogWhenCell(log)}
                                                            </td>
                                                            {showActivityColumn &&
                                                                <td>
                                                                    {renderAuditLogActivityCell(log, actionParts, statusDisplay)}
                                                                </td>
                                                            }
                                                            <td>
                                                                {!showActivityColumn &&
                                                                    <div className='watr-audit-log-entry-lead'>
                                                                        {renderAuditLogActivityCell(log, actionParts, statusDisplay)}
                                                                    </div>
                                                                }
                                                                {(() => {
                                                                    const detailBody = formatAuditLogDetailMessage(log);
                                                                    if (!detailBody) {
                                                                        return null;
                                                                    }
                                                                    if (!showActivityColumn && actionParts.detailLabel === detailBody) {
                                                                        return null;
                                                                    }
                                                                    return (
                                                                        <div className='watr-audit-log-entry-body'>
                                                                            {ReactHtmlParser(detailBody.replace(/(?:\r\n|\r|\n)/g, '<br/>'))}
                                                                        </div>
                                                                    );
                                                                })()}
                                                                {log.fileLoc && (
                                                                    <div style={{ marginTop: 6 }}>
                                                                        <Button
                                                                            bsSize='xsmall'
                                                                            onClick={() => handleDownloadFile(FileDocumentType.Notes, log.id)}>
                                                                            {/^Declined .+ cleared/i.test((log.logMessage || '').trim())
                                                                                ? 'Download declined file'
                                                                                : 'Download file'}
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                    </>
                                    :
                                    <div style={{ ...auditTablePanelStyle, padding: '20px 16px', textAlign: 'center' }}>
                                        <div style={{ fontWeight: 600, color: '#333', fontSize: 13, marginBottom: 8 }}>
                                            No audit entries match your filters
                                        </div>
                                        <div style={{ color: '#666', fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>
                                            Category: <strong>{getActiveAuditCategoryLabel()}</strong>
                                            {' · '}
                                            Source: <strong>{getActiveAuditSourceLabel()}</strong>
                                        </div>
                                        <div style={{ color: '#666', fontSize: 12, lineHeight: 1.55, marginBottom: 12 }}>
                                            Choose a category chip with a count above zero, or reset filters to view the full audit trail.
                                        </div>
                                        <div>
                                            <Button
                                                bsStyle='link'
                                                bsSize='xsmall'
                                                style={{ marginRight: 12 }}
                                                onClick={() => selectCategoryFilter(AUDIT_ACTION_CATEGORY.ApprovalProcess)}>
                                                Show LWR
                                            </Button>
                                            <Button
                                                bsStyle='link'
                                                bsSize='xsmall'
                                                style={{ marginRight: 12 }}
                                                onClick={() => selectCategoryFilter(AUDIT_ACTION_FILTER_ALL)}>
                                                Show all categories
                                            </Button>
                                            <Button
                                                bsStyle='link'
                                                bsSize='xsmall'
                                                onClick={() => selectSourceFilter(AUDIT_SOURCE_FILTER_ALL)}>
                                                Show all sources
                                            </Button>
                                        </div>
                                    </div>
                                )
                                :
                                <Well bsSize='sm'>
                                    No audit entries have been added yet....
                                </Well>
                            }
                        </>
                    }
                </Panel.Body>
            </Panel>
        </>
    );
};

export default AuditLog;
